/**
 * 75:2's 'O que esta conta publicou' card: the Relacionadas/Todas segmented list over `api`'s
 * `reading/publications` route (`AccountPublicationsOut`).
 *
 * The frame draws a view count, a comment count and a signed delta chip on every row;
 * `PostRowOut` carries none of them (post id, handle, label, platform, kind, timestamps, caption,
 * media only -- no metrics field at all). `align-fix-publications-api-post-figures` (still
 * `NOT_STARTED`) is the task that adds a related reach and its delta to this same shared
 * `PostRowOut`; until it lands, rows render without the metric badges rather than inventing them.
 *
 * `PostRowOut.kind` is `MediaKind` (`POST`/`REEL`/`STORY`), which cannot by itself distinguish a
 * photo from a video or a carousel -- the frame's finer labels ("foto", "vídeo", "carrossel",
 * "reels") are approximated from `kind` plus the media count (`>1` item reads as a carousel),
 * an ASSUMED heuristic pending a real `media_kind` breakdown.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { ChevronRight, ImageIcon } from 'lucide-react';

import { IconHint } from '../../../../chrome/hint';
import { EmptyState } from '../../../../chrome/state/EmptyState';
import { QueryStates } from '../../../../chrome/state/QueryStates';
import { getLocale } from '../../../../i18n';
import { integer } from '../../../../i18n/format';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Skeleton } from '../../../../ui/skeleton';
import { SegmentedControl } from '../../../data/publications/SegmentedControl';
import { useAccountPublications, type ReadingWindow } from '../api/queries';
import type { AccountPublications, PostRow } from '../api/schemas';
import { am } from '../messages';

type Tab = 'related' | 'all';

function kindLabel(post: PostRow): string {
  if (post.media.length > 1) return am('publications.kind.carousel');
  if (post.kind === 'REEL') return am('publications.kind.reel');
  if (post.kind === 'STORY') return am('publications.kind.story');
  return am('publications.kind.post');
}

function publishedAgo(iso: string, locale: string): string {
  const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-days, 'day');
}

function PostItem(props: { post: PostRow }): ReactNode {
  const locale = getLocale();
  const { post } = props;
  return (
    <li className="flex items-center gap-3 border-b border-border py-3 last:border-b-0">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
        <ImageIcon className="size-4 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm font-medium">
          {post.caption ?? am('publications.noCaption')}
        </p>
        <p className="text-caption text-muted-foreground">
          {publishedAgo(post.published_at, locale)}
          {/* eslint-disable-next-line no-restricted-syntax -- middot separator, identical in both locales */}
          {' · '}
          {kindLabel(post)}
        </p>
      </div>
      {post.url !== null && (
        <IconHint label={post.caption ?? am('publications.noCaption')}>
          <a
            href={post.url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight aria-hidden className="size-4" />
          </a>
        </IconHint>
      )}
    </li>
  );
}

function PublicationsList(props: { data: AccountPublications }): ReactNode {
  const locale = getLocale();
  const [tab, setTab] = useState<Tab>('related');
  const rows = tab === 'related' ? props.data.related : props.data.all_posts;

  return (
    <div className="space-y-3">
      <SegmentedControl
        label={am('publications.title')}
        value={tab}
        onChange={setTab}
        options={[
          {
            value: 'related',
            label: `${am('publications.related')} ${integer(props.data.related.length, locale)}`,
          },
          {
            value: 'all',
            label: `${am('publications.all')} ${integer(props.data.all_posts.length, locale)}`,
          },
        ]}
      />
      {rows.length === 0 ? (
        <EmptyState
          title={am('publications.empty.title')}
          description={am('publications.empty.description')}
        />
      ) : (
        <ul>
          {rows.map((post) => (
            <PostItem key={post.post_id} post={post} />
          ))}
        </ul>
      )}
    </div>
  );
}

function isEmptyPublications(data: AccountPublications): boolean {
  return data.related.length === 0 && data.all_posts.length === 0;
}

function PublicationsBody(): ReactNode {
  return (
    <div aria-hidden className="space-y-3">
      <Skeleton className="h-9 w-48 rounded-pill" />
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex items-center gap-3 py-2">
          <Skeleton className="size-10 shrink-0 rounded-md" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function PublicationsSkeleton(): ReactNode {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-56" />
      </CardHeader>
      <CardContent>
        <PublicationsBody />
      </CardContent>
    </Card>
  );
}

export function Publications(props: {
  campaignId: string;
  trackedAccountId: string;
  window: ReadingWindow;
}): ReactNode {
  const query = useAccountPublications(props.campaignId, props.trackedAccountId, props.window);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{am('publications.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryStates
          query={query}
          isEmpty={isEmptyPublications}
          loading={<PublicationsBody />}
          empty={
            <EmptyState
              title={am('publications.empty.title')}
              description={am('publications.empty.description')}
            />
          }
        >
          {(data) => <PublicationsList data={data} />}
        </QueryStates>
      </CardContent>
    </Card>
  );
}
